import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import type { ICampaignParticipantDetail } from '../../../redux/buzzbridge/types';
import {
    approveCampaignJoinRequest,
    broadcastCampaignParticipantMessage,
    cancelCampaignInvite,
} from '../../../redux/buzzbridge/actions';
import {
    approvingRequestSelector,
    broadcastingCampaignParticipantMessageSelector,
    cancellingInviteSelector,
} from '../../../redux/buzzbridge/selectors';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
} from '../brandDashboardTokens';
import { ApplicationReviewDrawer } from './ApplicationReviewDrawer';
import { RescindApprovalDialog } from './RescindApprovalDialog';
import { CampaignThreadUnreadBadge } from '../../../components/BuzzBridge/CampaignThreadUnreadBadge';
import { participantUnreadCount } from '../../../utils/campaignThreadUnread';
import {
    influencerProfileAvatarSx,
    influencerProfilePhotoCoverSx,
} from '../../../components/BuzzBridge/utils/creatorProfileHelpers';
import { useApplicantFavorite } from './useApplicantFavorite';

const CONTENT_STATUS_COLORS: Record<string, string> = {
    'Content Pending': BRAND_LUXE_SECONDARY,
    Delivered: BRAND_LUXE_PRIMARY,
    'In Review': BRAND_LUXE_PRIMARY,
    submitted: BRAND_LUXE_SECONDARY,
    approved: BRAND_LUXE_SECONDARY,
    needs_revision: '#ff7351',
    draft: 'rgba(255,255,255,0.4)',
};

function participantPath(campaignId: string, participantId: string) {
    return ROUTES.BRAND_CAMPAIGN_PARTICIPANT.replace(':campaignId', campaignId).replace(
        ':participantId',
        participantId,
    );
}

function tagLabel(tag: string | { main: string; sub?: string }): string {
    if (typeof tag === 'string') return tag;
    return tag.sub ? `${tag.main} · ${tag.sub}` : tag.main;
}

export function CampaignParticipants({
    participants,
    pendingRequests,
    pendingInvites,
    campaignId,
    requirements = [],
}: {
    participants: ICampaignParticipantDetail[];
    pendingRequests: ICampaignParticipantDetail[];
    pendingInvites?: ICampaignParticipantDetail[];
    campaignId: string;
    requirements?: string[];
}) {
    const safeParticipants = participants || [];
    const safePendingRequests = pendingRequests || [];
    const safePendingInvites = pendingInvites || [];

    return (
        <Box
            id="campaign-participants"
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <NewApplications applicants={safePendingRequests} campaignId={campaignId} />
                <PendingInvites invites={safePendingInvites} campaignId={campaignId} />
            </Box>
            <AcceptedCreators creators={safeParticipants} campaignId={campaignId} requirements={requirements} />
        </Box>
    );
}

/* ──────── New Applications ──────── */

function NewApplications({ applicants, campaignId }: { applicants: ICampaignParticipantDetail[]; campaignId: string }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [drawerApplicant, setDrawerApplicant] = useState<ICampaignParticipantDetail | null>(null);
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const approvingRequest = useSelector(approvingRequestSelector);
    const preview = applicants.slice(0, 2);

    useEffect(() => {
        if (!approvingRequest && acceptingId) {
            setAcceptingId(null);
        }
    }, [approvingRequest, acceptingId]);

    const handleAcceptFromCard = (applicant: ICampaignParticipantDetail) => {
        setAcceptingId(applicant._id);
        dispatch(
            approveCampaignJoinRequest({
                requestId: applicant._id,
                campaignId,
            }),
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', letterSpacing: '-0.01em' }}>
                    Applications ({applicants.length})
                </Typography>
                {applicants.length > 1 && (
                    <Button
                        onClick={() =>
                            navigate(
                                `${ROUTES.BRAND_CAMPAIGN_APPLICATIONS.replace(
                                    ':campaignId',
                                    campaignId,
                                )}?tab=applications`,
                            )
                        }
                        sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: BRAND_LUXE_PRIMARY,
                            textDecoration: 'underline',
                            p: 0,
                            '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                        }}>
                        View all
                    </Button>
                )}
            </Box>

            {applicants.length === 0 ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 4, textAlign: 'center' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                        No pending applications
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                        {preview.map(a => (
                            <ApplicationCard
                                key={a._id}
                                applicant={a}
                                campaignId={campaignId}
                                onReview={() => setDrawerApplicant(a)}
                                onAccept={() => handleAcceptFromCard(a)}
                                isAccepting={acceptingId === a._id && approvingRequest}
                            />
                        ))}
                    </Box>
                </>
            )}

            <ApplicationReviewDrawer
                open={Boolean(drawerApplicant)}
                onClose={() => setDrawerApplicant(null)}
                applicant={drawerApplicant}
                campaignId={campaignId}
            />
        </Box>
    );
}

/* ──────── Pending Invites ──────── */

function PendingInvites({ invites, campaignId }: { invites: ICampaignParticipantDetail[]; campaignId: string }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [drawerInvite, setDrawerInvite] = useState<ICampaignParticipantDetail | null>(null);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const cancellingInvite = useSelector(cancellingInviteSelector);
    const preview = invites.slice(0, 2);

    useEffect(() => {
        if (!cancellingInvite && cancellingId) {
            setCancellingId(null);
        }
    }, [cancellingInvite, cancellingId]);

    const handleCancel = (invite: ICampaignParticipantDetail) => {
        setCancellingId(invite._id);
        dispatch(cancelCampaignInvite({ participantId: invite._id, campaignId }));
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', letterSpacing: '-0.01em' }}>
                    Invites ({invites.length})
                </Typography>
                {invites.length > 1 && (
                    <Button
                        onClick={() =>
                            navigate(
                                `${ROUTES.BRAND_CAMPAIGN_APPLICATIONS.replace(':campaignId', campaignId)}?tab=invites`,
                            )
                        }
                        sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: BRAND_LUXE_PRIMARY,
                            textDecoration: 'underline',
                            p: 0,
                            '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                        }}>
                        View all
                    </Button>
                )}
            </Box>

            {invites.length === 0 ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 4, textAlign: 'center' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                        No pending invites
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    {preview.map(invite => (
                        <ApplicationCard
                            key={invite._id}
                            applicant={invite}
                            campaignId={campaignId}
                            onReview={() => setDrawerInvite(invite)}
                            onAccept={() => undefined}
                            onCancelInvite={() => handleCancel(invite)}
                            isAccepting={cancellingId === invite._id && cancellingInvite}
                        />
                    ))}
                </Box>
            )}

            <ApplicationReviewDrawer
                open={Boolean(drawerInvite)}
                onClose={() => setDrawerInvite(null)}
                applicant={drawerInvite}
                campaignId={campaignId}
                inviteMode
            />
        </Box>
    );
}

export function ApplicationCard({
    applicant,
    campaignId,
    onReview,
    onAccept,
    onReject,
    onCancelInvite,
    isAccepting,
    showActions = true,
    onClick,
    selected,
    highlighted,
    onToggleSelect,
    onFavoriteChange,
}: {
    applicant: ICampaignParticipantDetail;
    campaignId: string;
    onReview: () => void;
    onAccept: () => void;
    onReject?: () => void;
    /** Host cancel pending invite — replaces Accept when set. */
    onCancelInvite?: () => void;
    isAccepting?: boolean;
    showActions?: boolean;
    onClick?: () => void;
    selected?: boolean;
    /** Last creator opened into the workspace — kept after navigating back. */
    highlighted?: boolean;
    onToggleSelect?: () => void;
    /** Notified when favourite state changes (for favorites-only filter). */
    onFavoriteChange?: (influencerId: string, favorited: boolean) => void;
}) {
    const safeTags = applicant.tags || [];
    const initials =
        (applicant.name || '')
            .split(' ')
            .map(w => w[0])
            .join('')
            .slice(0, 2) || 'NA';
    const { favorited, toggleFavorite } = useApplicantFavorite(
        campaignId,
        applicant.influencer_id,
        applicant.is_favorited,
    );

    const handleFavorite = async (e: React.MouseEvent) => {
        const next = !favorited;
        if (applicant.influencer_id) onFavoriteChange?.(applicant.influencer_id, next);
        const ok = await toggleFavorite(e);
        if (!ok && applicant.influencer_id) onFavoriteChange?.(applicant.influencer_id, !next);
    };

    return (
        <Box
            id={highlighted ? `accepted-creator-${applicant._id}` : undefined}
            data-creator-id={applicant._id}
            onClick={onClick}
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: 3,
                border: `1px solid ${
                    highlighted ? BRAND_LUXE_PRIMARY : selected ? `${BRAND_LUXE_PRIMARY}50` : 'rgba(255,255,255,0.05)'
                }`,
                boxShadow: highlighted ? `0 0 0 2px ${BRAND_LUXE_PRIMARY}, 0 0 28px rgba(255,201,101,0.35)` : 'none',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': {
                    borderColor: highlighted || selected ? BRAND_LUXE_PRIMARY : 'rgba(255,201,101,0.15)',
                },
            }}>
            {/* Top: avatar banner */}
            <Box sx={{ position: 'relative', height: 72, bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST, flexShrink: 0 }}>
                {applicant.avatar && (
                    <Box
                        component="img"
                        src={applicant.avatar}
                        sx={{ width: '100%', height: '100%', opacity: 0.25, ...influencerProfilePhotoCoverSx }}
                    />
                )}
                {/* Selection checkbox */}
                {onToggleSelect && (
                    <Checkbox
                        checked={!!selected}
                        onChange={e => {
                            e.stopPropagation();
                            onToggleSelect();
                        }}
                        onClick={e => e.stopPropagation()}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            p: 0.5,
                            color: 'rgba(255,255,255,0.4)',
                            '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                            '& .MuiSvgIcon-root': { fontSize: 18 },
                        }}
                    />
                )}
                {/* Favourite heart — top right */}
                {applicant.influencer_id && (
                    <IconButton
                        size="small"
                        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                        onClick={e => void handleFavorite(e)}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 1,
                            bgcolor: 'rgba(0,0,0,0.45)',
                            color: favorited ? '#ff69b4' : 'rgba(255,255,255,0.85)',
                            border: favorited ? '1px solid rgba(255,105,180,0.5)' : '1px solid rgba(255,255,255,0.12)',
                            '&:hover': { bgcolor: 'rgba(255,105,180,0.18)', color: '#ff69b4' },
                        }}>
                        {favorited ? (
                            <FavoriteIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                )}
                {/* Avatar */}
                <Box sx={{ position: 'absolute', bottom: -28, left: 20 }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Avatar
                            src={applicant.avatar || undefined}
                            sx={{
                                width: 56,
                                height: 56,
                                border: `3px solid ${BRAND_SURFACE_CONTAINER}`,
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                color: BRAND_LUXE_PRIMARY,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 18,
                                ...influencerProfileAvatarSx,
                            }}>
                            {initials}
                        </Avatar>
                        <CampaignThreadUnreadBadge count={participantUnreadCount(applicant)} size="sm" overlay />
                    </Box>
                </Box>
                {/* Engagement rate badge */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 14,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 20,
                            color: BRAND_LUXE_SECONDARY,
                            lineHeight: 1,
                        }}>
                        {applicant.engagementRate}%
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 9,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: 'rgba(255,255,255,0.4)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                        }}>
                        Eng. Rate
                    </Typography>
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ pt: 4.5, px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                {/* Name + handle */}
                <Box>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
                        {applicant.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.4)',
                            mt: 0.25,
                        }}>
                        {applicant.handle}
                    </Typography>
                    {(applicant.commission_code || applicant.commission?.code) && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                color: BRAND_LUXE_PRIMARY,
                                mt: 0.75,
                            }}>
                            Code: {applicant.commission_code || applicant.commission?.code}
                            {applicant.commission?.share_url ? (
                                <Box
                                    component="a"
                                    href={applicant.commission.share_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ ml: 1, color: 'rgba(255,201,101,0.85)', textDecoration: 'underline' }}>
                                    Share link
                                </Box>
                            ) : null}
                        </Typography>
                    )}
                </Box>

                {/* Tags */}
                {safeTags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {safeTags.slice(0, 3).map((tag, i) => (
                            <Chip
                                key={i}
                                label={tagLabel(tag)}
                                size="small"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    height: 22,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: 'rgba(255,255,255,0.6)',
                                    border: 'none',
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Pitch */}
                {applicant.pitch && (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.45)',
                            fontStyle: 'italic',
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}>
                        &ldquo;{applicant.pitch}&rdquo;
                    </Typography>
                )}

                {/* Preview video pill */}
                {applicant.previewVideo && (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            alignSelf: 'flex-start',
                            bgcolor: 'rgba(255,255,255,0.06)',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 14, color: BRAND_LUXE_PRIMARY }} />
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: BRAND_LUXE_PRIMARY,
                                letterSpacing: '0.04em',
                            }}>
                            Preview Video
                        </Typography>
                    </Box>
                )}

                {/* Actions */}
                {showActions && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 'auto', pt: 1 }}>
                        {onReject ? (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onReview();
                                    }}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                        color: 'rgba(255,255,255,0.7)',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                            bgcolor: 'rgba(255,255,255,0.04)',
                                        },
                                    }}>
                                    Open
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onReject();
                                    }}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        borderColor: 'rgba(255,107,107,0.45)',
                                        color: '#ff8a80',
                                        '&:hover': {
                                            borderColor: 'rgba(255,107,107,0.7)',
                                            bgcolor: 'rgba(255,107,107,0.08)',
                                        },
                                    }}>
                                    Reject
                                </Button>
                            </>
                        ) : onCancelInvite ? (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onReview();
                                    }}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                        color: 'rgba(255,255,255,0.7)',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                            bgcolor: 'rgba(255,255,255,0.04)',
                                        },
                                    }}>
                                    Review
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onCancelInvite();
                                    }}
                                    disabled={isAccepting}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        borderColor: 'rgba(255,107,107,0.45)',
                                        color: '#ff8a80',
                                        '&:hover': {
                                            borderColor: 'rgba(255,107,107,0.7)',
                                            bgcolor: 'rgba(255,107,107,0.08)',
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: 'rgba(255,107,107,0.2)',
                                            color: 'rgba(255,138,128,0.4)',
                                        },
                                    }}>
                                    {isAccepting ? (
                                        <CircularProgress size={15} thickness={5} sx={{ color: '#ff8a80' }} />
                                    ) : (
                                        'Cancel'
                                    )}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={onReview}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                        color: 'rgba(255,255,255,0.7)',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                            bgcolor: 'rgba(255,255,255,0.04)',
                                        },
                                    }}>
                                    Review
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={onAccept}
                                    disabled={isAccepting}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        letterSpacing: '0.08em',
                                        bgcolor: BRAND_LUXE_PRIMARY,
                                        color: '#392700',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: '#feb700',
                                            boxShadow: '0 4px 12px rgba(255,193,7,0.2)',
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: `${BRAND_LUXE_PRIMARY}50`,
                                            color: '#39270060',
                                        },
                                    }}>
                                    {isAccepting ? (
                                        <CircularProgress size={15} thickness={5} sx={{ color: '#392700' }} />
                                    ) : (
                                        'Accept'
                                    )}
                                </Button>
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

/* ──────── Accepted Creators ──────── */

function canRescindApproval(creator: ICampaignParticipantDetail): boolean {
    const status = String(creator.status || 'approved').toLowerCase();
    return status === 'approved' || status === 'in_progress';
}

function AcceptedCreators({
    creators,
    campaignId,
    requirements = [],
}: {
    creators: ICampaignParticipantDetail[];
    campaignId: string;
    requirements?: string[];
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const preview = creators.slice(0, 12);
    const [rejectCreator, setRejectCreator] = useState<ICampaignParticipantDetail | null>(null);
    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [broadcastText, setBroadcastText] = useState('');
    const broadcasting = useSelector(broadcastingCampaignParticipantMessageSelector);

    const sendBroadcast = () => {
        const content = broadcastText.trim();
        if (!content || !campaignId) return;
        dispatch(broadcastCampaignParticipantMessage({ campaignId, content }));
        setBroadcastText('');
        setBroadcastOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2.5,
                    gap: 1,
                    flexWrap: 'wrap',
                }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', letterSpacing: '-0.01em' }}>
                    Accepted Creators ({creators.length})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {creators.length > 0 && (
                        <Button
                            onClick={() => setBroadcastOpen(true)}
                            disabled={broadcasting}
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: '#131517',
                                bgcolor: BRAND_LUXE_PRIMARY,
                                borderRadius: 999,
                                px: 1.75,
                                py: 0.5,
                                '&:hover': { bgcolor: BRAND_LUXE_PRIMARY, opacity: 0.9 },
                            }}>
                            {broadcasting ? 'Queuing…' : 'Message all'}
                        </Button>
                    )}
                    {creators.length > 1 && (
                        <Button
                            onClick={() =>
                                navigate(ROUTES.BRAND_CAMPAIGN_ACCEPTED_CREATORS.replace(':campaignId', campaignId))
                            }
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: BRAND_LUXE_PRIMARY,
                                textDecoration: 'underline',
                                p: 0,
                                '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                            }}>
                            View all
                        </Button>
                    )}
                </Box>
            </Box>
            <Dialog
                open={broadcastOpen}
                onClose={() => !broadcasting && setBroadcastOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        backgroundImage: 'none',
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.08)',
                        minWidth: { xs: '90vw', sm: 420 },
                    },
                }}>
                <DialogTitle sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                    Message all creators
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, mb: 1.5 }}>
                        Sends to approved, in-progress, and completed participants. Each creator gets a campaign thread
                        message and the usual email if unread.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={4}
                        value={broadcastText}
                        onChange={e => setBroadcastText(e.target.value)}
                        placeholder="Write your message…"
                        sx={{
                            textarea: { color: '#fff' },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setBroadcastOpen(false)}
                        disabled={broadcasting}
                        sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={sendBroadcast}
                        disabled={broadcasting || !broadcastText.trim()}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: BRAND_LUXE_PRIMARY,
                            color: '#131517',
                            '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                        }}>
                        {broadcasting ? 'Queuing…' : 'Send to all'}
                    </Button>
                </DialogActions>
            </Dialog>
            {creators.length === 0 ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 4, textAlign: 'center' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                        No accepted creators yet
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {preview.map(c => (
                        <AcceptedCreatorChip
                            key={c._id}
                            creator={c}
                            campaignId={campaignId}
                            onReject={canRescindApproval(c) ? () => setRejectCreator(c) : undefined}
                        />
                    ))}
                </Box>
            )}
            <RescindApprovalDialog
                open={Boolean(rejectCreator)}
                onClose={() => setRejectCreator(null)}
                creator={rejectCreator}
                campaignId={campaignId}
                requirements={requirements}
            />
        </Box>
    );
}

function AcceptedCreatorChip({
    creator,
    campaignId,
    onReject,
}: {
    creator: ICampaignParticipantDetail;
    campaignId: string;
    onReject?: () => void;
}) {
    const navigate = useNavigate();
    const initials = creator.name
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2);
    const statusColor = CONTENT_STATUS_COLORS[creator.contentStatus || ''] || BRAND_LUXE_SECONDARY;

    return (
        <Box
            onClick={() => navigate(participantPath(campaignId, creator._id))}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                pl: 0.5,
                pr: onReject ? 1 : 2,
                py: 0.5,
                borderRadius: 999,
                border: `1px solid ${BRAND_OUTLINE_VARIANT}1A`,
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: 'rgba(255,201,101,0.2)' },
            }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                    src={creator.avatar || undefined}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        color: BRAND_LUXE_PRIMARY,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        ...influencerProfileAvatarSx,
                    }}>
                    {initials}
                </Avatar>
                <CampaignThreadUnreadBadge count={participantUnreadCount(creator)} size="sm" overlay />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff', lineHeight: 1.2 }}
                    noWrap>
                    {creator.name}
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: statusColor, lineHeight: 1.2 }}>
                    {creator.contentStatus || 'Accepted'}
                </Typography>
            </Box>
            {onReject ? (
                <Button
                    size="small"
                    onClick={e => {
                        e.stopPropagation();
                        onReject();
                    }}
                    sx={{
                        ml: 0.5,
                        minWidth: 0,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        color: '#ff8a80',
                        border: '1px solid rgba(255,107,107,0.35)',
                        '&:hover': { bgcolor: 'rgba(255,107,107,0.1)', borderColor: 'rgba(255,107,107,0.55)' },
                    }}>
                    Reject
                </Button>
            ) : null}
        </Box>
    );
}
